/**
 * Notificaciones de Legal Flow a partir de `legal_flow_notificaciones` (la campana del portal y
 * la pantalla Notificaciones). La RPC devuelve los datos; aquí se arman títulos, textos, nivel y
 * urgencia, con las mismas reglas que tenía el hook cuando juntaba tres consultas en el navegador.
 * Sin Supabase, para probarlo solo.
 */

export type TipoNotificacionLegal =
  | "solicitud_recibida"
  | "expediente_en_revision"
  | "fecha_firma_por_aprobar"
  | "firma_titular_pendiente"
  | "documento_rechazado"
  | "documento_validado";

export type NivelNotificacionLegal = "info" | "warning" | "success" | "error";

export interface NotificacionLegalFlow {
  id: string;
  tipo: TipoNotificacionLegal;
  nivel: NivelNotificacionLegal;
  titulo: string;
  mensaje: string;
  /** Folio CC-XXXXXX para navegar al detalle ("" si la entrada no es de una cuenta). */
  folio_cuenta: string;
  fecha_evento: string;
  dias_esperando: number;
  critico: boolean;
}

/** Lo que devuelve la RPC. */
export interface NotificacionesRpc {
  expedientes: Array<{
    tipo:
      | "solicitud_recibida"
      | "expediente_en_revision"
      | "fecha_firma_por_aprobar"
      | "firma_titular_pendiente";
    id_cuenta_cobranza: number;
    folio: string;
    etapa: string;
    proyecto: string | null;
    numero_propiedad: string | null;
    comprador: string | null;
    fecha_evento: string;
  }>;
  bitacora: Array<{
    id: string;
    tipo: "documento_rechazado" | "documento_validado";
    id_cuenta_cobranza: number | null;
    folio: string | null;
    mensaje: string | null;
    autor: string;
    fecha_evento: string;
  }>;
}

/** Una solicitud nueva sin revisar más de estos días es crítica. */
const DIAS_CRITICO_SOLICITUD = 7;
/** Un contrato firmado esperando validación más de estos días es crítico. */
const DIAS_CRITICO_FIRMA = 3;
/** Una fecha de firma propuesta por el cliente sin resolver más de estos días es crítica. */
const DIAS_CRITICO_FECHA_FIRMA = 2;

export function diasDesde(iso: string | null | undefined, ahora: number = Date.now()): number {
  if (!iso) return 0;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return 0;
  return Math.max(0, Math.floor((ahora - t) / 86_400_000));
}

export function componerNotificaciones(rpc: NotificacionesRpc, ahora: number = Date.now()): NotificacionLegalFlow[] {
  const out: NotificacionLegalFlow[] = [];

  for (const e of rpc.expedientes) {
    const dias = diasDesde(e.fecha_evento, ahora);
    const mensaje =
      [e.proyecto || "Sin proyecto", e.numero_propiedad ? `Unidad ${e.numero_propiedad}` : null, e.comprador]
        .filter(Boolean)
        .join(" · ") || "Expediente legal pendiente";
    if (e.tipo === "fecha_firma_por_aprobar") {
      out.push({
        id: `fecha-firma:${e.id_cuenta_cobranza}`,
        tipo: e.tipo,
        nivel: "warning",
        titulo: `Fecha de firma por aprobar · ${e.folio}`,
        mensaje,
        folio_cuenta: e.folio,
        fecha_evento: e.fecha_evento,
        dias_esperando: dias,
        critico: dias > DIAS_CRITICO_FECHA_FIRMA,
      });
      continue;
    }
    if (e.tipo === "firma_titular_pendiente") {
      out.push({
        id: `firma-titular:${e.id_cuenta_cobranza}`,
        tipo: e.tipo,
        nivel: "warning",
        titulo: `Contrato pendiente de validación · ${e.folio}`,
        mensaje,
        folio_cuenta: e.folio,
        fecha_evento: e.fecha_evento,
        dias_esperando: dias,
        critico: dias > DIAS_CRITICO_FIRMA,
      });
      continue;
    }
    const enRevision = e.tipo === "expediente_en_revision";
    out.push({
      id: `solicitud:${e.id_cuenta_cobranza}`,
      tipo: e.tipo,
      nivel: enRevision ? "info" : "warning",
      titulo: enRevision ? `Expediente en revisión legal · ${e.folio}` : `Solicitud nueva por revisar · ${e.folio}`,
      mensaje,
      folio_cuenta: e.folio,
      fecha_evento: e.fecha_evento,
      dias_esperando: dias,
      critico: !enRevision && dias > DIAS_CRITICO_SOLICITUD,
    });
  }

  for (const b of rpc.bitacora) {
    const rechazo = b.tipo === "documento_rechazado";
    const folio = b.folio ?? "";
    // Convención de la bitácora: "<titulo>\n\n<descripcion>" en `mensaje`.
    const [titulo, ...resto] = String(b.mensaje ?? "").split("\n\n");
    const texto = [titulo, resto.join("\n\n")].filter(Boolean).join(" - ");
    const autor = b.autor || "Sistema";
    out.push({
      id: `bitacora:${b.id}`,
      tipo: b.tipo,
      nivel: rechazo ? "error" : "success",
      titulo: `${rechazo ? "Rechazo registrado" : "Documento validado"}${folio ? ` · ${folio}` : ""}`,
      mensaje: texto ? `${texto} · por ${autor}` : `Por ${autor}`,
      folio_cuenta: folio,
      fecha_evento: b.fecha_evento,
      dias_esperando: diasDesde(b.fecha_evento, ahora),
      critico: rechazo,
    });
  }

  // Críticas primero; luego lo más reciente.
  return out.sort((a, b) => {
    if (a.critico !== b.critico) return a.critico ? -1 : 1;
    return new Date(b.fecha_evento).getTime() - new Date(a.fecha_evento).getTime();
  });
}
